import React, { useEffect, useMemo, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import DataTable from '../DataTable';
import { Pen, Trash } from 'react-bootstrap-icons';
import AlertModal from '../AlertModal';
import { Plus } from 'lucide-react';
import AddEditCityContactModal from './AddEditCityContactModal';
import useMunicipalityCounty from '../../hooks/useMunicipalityCounty';

const cityDepartmentHeaders = [
  { name: 'City', minWidth: 200, render: contact => <span className="font-weight-bold">{contact.City}</span> },
  { name: 'Department', minWidth: 150 },
  { name: 'Contact Numbers', minWidth: 200 },
  { name: 'Contact Emails', minWidth: 250 },
  { name: 'County', minWidth: 120 }
];

const countyDepartmentHeaders = [
  { name: 'Department', minWidth: 150 },
  { name: 'Contact Numbers', minWidth: 200 },
  { name: 'Contact Emails', minWidth: 250 },
  { name: 'County', minWidth: 120 }
];

const providerHeaders = [
  { name: 'Provider Name', minWidth: 200 },
  { name: 'City', minWidth: 150 },
  { name: 'Service', minWidth: 150 },
  { name: 'Contact Numbers', minWidth: 200 },
  { name: 'Contact Emails', minWidth: 250 },
  { name: 'Mail Payment To', minWidth: 200 },
  { name: 'How to Pay', minWidth: 150 },
  { name: 'Make Check Payable To', minWidth: 200 },
  { name: 'Pay Online URL', minWidth: 200, linkToKey: 'Pay Online URL' },
  { name: 'County', minWidth: 120 }
];

const toArray = value => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

const getInitialContactFormValues = ({ contact, city, state }) => {
  if (!contact) {
    return {
      providerName: '',
      mailPaymentTo: '',
      howToPay: [],
      makeCheckPayableTo: '',
      payOnlineUrl: '',
      city: city || '',
      state: state || '',
      department: [],
      contactNumbers: [],
      contactEmails: []
    };
  }

  return {
    providerName: contact['Provider Name'] || contact.providerName || '',
    mailPaymentTo: contact['Mail Payment To'] || contact.mailPaymentTo || '',
    howToPay: toArray(contact['How to Pay'] || contact.howToPay),
    makeCheckPayableTo: contact['Make Check Payable To'] || contact.makeCheckPayableTo || '',
    payOnlineUrl: contact['Pay Online URL'] || contact.payOnlineUrl || '',
    city: contact.City || contact.city || city || '',
    state: contact.County || contact.state || state || '',
    department: toArray(contact.Service || contact.Department || contact.department),
    contactNumbers: toArray(contact['Contact Numbers'] || contact.contactNumbers),
    contactEmails: toArray(contact['Contact Emails'] || contact.contactEmails)
  };
};

const contactTabs = ['City Department', 'County Department', 'Provider'];

const CityContacts = ({ syncedTab = null, city = '', state = '', setActiveTabItemsMetadata }) => {
  const activeTab =
    syncedTab && contactTabs.includes(syncedTab) ? syncedTab : contactTabs[0];
  const [contactActionMeta, setContactActionMeta] = useState(null);
  const [toBeDeletedItem, setToBeDeletedItem] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [toLoadPageInfo, setToLoadPageInfo] = useState({
    pageNumber: 1
  });
  const [loadedPageInfo, setLoadedPageInfo] = useState({
    totalPages: 1,
    resultCount: 0,
    pageNumber: 1
  });

  const [searchQuery, setSearchQuery] = useState('');
  const { refreshRecords } = useMunicipalityCounty()

  const fetchContacts = async activeTab => {
    setContacts([]);
    setIsFetchingData(true);
    if (setActiveTabItemsMetadata) {
      setActiveTabItemsMetadata({ count: 0, loading: true });
    }
    const requestBody = {
      pageSize: 15,
      pageNumber: toLoadPageInfo.pageNumber
    };

    if (searchQuery?.trim()) {
      requestBody.query = searchQuery;
    }

    if (city) {
      requestBody.filters = [...(requestBody.filters || []), { key: 'city', value: city, }];
    }

    if (state) {
      requestBody.filters = [...(requestBody.filters || []), { key: 'state', value: state, }];
    }

    const endpoint = {
      'City Department': ENDPOINTS.CITY_DEPARTMENT_CONTACTS_SEARCH,
      'County Department': ENDPOINTS.COUNTY_DEPARTMENT_CONTACTS_SEARCH,
      Provider: ENDPOINTS.PROVIDER_CONTACTS_SEARCH
    };

    const { response, error } = await makeRESTApiRequest({
      endpoint: endpoint[activeTab],
      method: 'POST',
      requestBody
    });

    if (error) {
      toast.error(error);
      setIsFetchingData(false);
      return;
    }

    setIsFetchingData(false);

    if (response) {
      const { pageNumber, totalPages, results = [], resultCount } = response;

      setLoadedPageInfo({ pageNumber, totalPages, resultCount });
      setContacts(results);
      if (setActiveTabItemsMetadata) {
        setActiveTabItemsMetadata({ count: resultCount, loading: false });
      }
    }
  };

  const handlePageChange = pageNumber => {
    setToLoadPageInfo({ ...toLoadPageInfo, pageNumber });
  };

  const handleSearch = query => {
    setSearchQuery(query);
  };

  useEffect(() => {
    setToLoadPageInfo(
      prev => ({ ...prev, pageNumber: 1 })
    );
  }, [searchQuery, city, state, activeTab]);

  useEffect(() => {
    fetchContacts(activeTab);
  }, [activeTab, toLoadPageInfo.pageNumber, searchQuery, city, state]);

  const tableData = useMemo(() => {
    const data = {
      Provider: contacts.map(contact => ({
        _id: contact._id,
        'Provider Name': contact.providerName,
        'Mail Payment To': contact.mailPaymentTo,
        'How to Pay': toArray(contact.howToPay),
        'Make Check Payable To': contact.makeCheckPayableTo,
        'Pay Online URL': contact.payOnlineUrl,
        Service: contact.department,
        'Contact Numbers': contact.contactNumbers,
        'Contact Emails': contact.contactEmails,
        County: contact.state,
        City: contact.city
      })),
      'City Department': contacts.map(contact => ({
        _id: contact._id,
        County: contact.state,
        City: contact.city,
        Department: contact.department,
        'Contact Numbers': contact.contactNumbers,
        'Contact Emails': contact.contactEmails
      })),
      'County Department': contacts.map(contact => ({
        _id: contact._id,
        County: contact.state,
        Department: contact.department,
        'Contact Numbers': contact.contactNumbers,
        'Contact Emails': contact.contactEmails
      }))
    };

    return data[activeTab];
  }, [contacts, activeTab]);

  const onAddContactFormSubmit = async form => {
    setLoading(true);

    const requestBody =
      activeTab === 'Provider'
        ? {
          providerName: form.providerName,
          mailPaymentTo: form.mailPaymentTo,
          howToPay: form.howToPay,
          makeCheckPayableTo: form.makeCheckPayableTo,
          payOnlineUrl: form.payOnlineUrl,
          state: form.state,
          city: form.city,
          department: form.department,
          contactNumbers: form.contactNumbers,
          contactEmails: form.contactEmails
        }
        : activeTab === 'City Department'
          ? {
            city: form.city,
            state: form.state,
            department: form.department,
            contactNumbers: form.contactNumbers,
            contactEmails: form.contactEmails
          }
          : {
            state: form.state,
            department: form.department,
            contactNumbers: form.contactNumbers,
            contactEmails: form.contactEmails
          };

    const { response, error } = await makeRESTApiRequest({
      endpoint:
        activeTab === 'Provider'
          ? ENDPOINTS.CREATE_PROVIDER_CONTACTS
          : activeTab === 'City Department'
            ? ENDPOINTS.CREATE_CITY_DEPARTMENT_CONTACTS
            : ENDPOINTS.CREATE_COUNTY_DEPARTMENT_CONTACTS,
      method: 'POST',
      requestBody
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }

    setLoading(false);

    if (response) {
      setContacts([...contacts, response]);
      refreshRecords();
    }

    toast.success('Department Contact Successfully Added');
    setContactActionMeta(null);
    fetchContacts(activeTab);
  };

  const onUpdateContactFormSubmit = async form => {
    setLoading(true);

    const requestBody =
      activeTab === 'Provider'
        ? {
          providerName: form.providerName,
          mailPaymentTo: form.mailPaymentTo,
          howToPay: form.howToPay,
          makeCheckPayableTo: form.makeCheckPayableTo,
          payOnlineUrl: form.payOnlineUrl,
          state: form.state,
          city: form.city,
          department: form.department,
          contactNumbers: form.contactNumbers,
          contactEmails: form.contactEmails
        }
        : activeTab === 'City Department'
          ? {
            city: form.city,
            state: form.state,
            department: form.department,
            contactNumbers: form.contactNumbers,
            contactEmails: form.contactEmails
          }
          : {
            state: form.state,
            department: form.department,
            contactNumbers: form.contactNumbers,
            contactEmails: form.contactEmails
          };

    const { response, error } = await makeRESTApiRequest({
      endpoint:
        activeTab === 'Provider'
          ? ENDPOINTS.PROVIDER_CONTACTS_WITH_ID(contactActionMeta.contact._id)
          : activeTab === 'City Department'
            ? ENDPOINTS.DEPARTMENT_CONTACTS_WITH_ID(contactActionMeta.contact._id)
            : ENDPOINTS.COUNTY_DEPARTMENT_CONTACTS_WITH_ID(contactActionMeta.contact._id),
      method: 'PUT',
      requestBody
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }

    setLoading(false);

    if (response) {
      setContacts([...contacts, response]);
      refreshRecords();
    }

    toast.success('Department Contact Successfully Updated');
    setContactActionMeta(null);

    fetchContacts(activeTab);
  };

  const handleDeleteContact = async id => {
    setLoading(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint:
        activeTab === 'Provider'
          ? ENDPOINTS.PROVIDER_CONTACTS_WITH_ID(id)
          : activeTab === 'City Department'
            ? ENDPOINTS.DEPARTMENT_CONTACTS_WITH_ID(id)
            : ENDPOINTS.COUNTY_DEPARTMENT_CONTACTS_WITH_ID(id),
      method: 'DELETE'
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }

    if (response) {
      setContacts(contacts.filter(contact => contact._id !== id));
    }

    setShowDeleteAlert(false);

    toast.success('Department Contact Successfully Deleted');
    setLoading(false);
    fetchContacts(activeTab);
  };

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <div
        className="pt-3 pb-3"
        style={{ paddingLeft: 24, paddingRight: 24 }}
      >

        <div className="bg-white">
          <DataTable
            searchHint="Search by state, city, department, phone, or email..."
            headers={
              activeTab === 'Provider'
                ? providerHeaders
                : activeTab === 'City Department'
                  ? cityDepartmentHeaders
                  : countyDepartmentHeaders
            }
            data={tableData}
            rowKeyField="_id"
            searchQueryKeys={tableData[0] ? Object.keys(tableData[0]) : []}
            onSearch={handleSearch}
            pagination={loadedPageInfo}
            loadingData={isFetchingData}
            onPageChange={handlePageChange}
            useInternalSearch={false}
            additionalComponents={() => (
              <Button
                variant="success"
                size="sm"
                className="d-flex align-items-center"
                style={{ minHeight: '40px' }}
                onClick={() => setContactActionMeta({ mode: 'add' })}
              >
                <Plus size={14} className="mr-1" />
                Add New
              </Button>
            )}
            actionCell={contact => (
              <div className="d-flex flex-nowrap align-items-center">
                <Button
                  variant="outline-dark"
                  className="px-1 py-0 mr-1"
                  onClick={() => {
                    setContactActionMeta({
                      contact: contacts.find(c => c._id === contact._id) || contact,
                      mode: 'edit'
                    });
                  }}
                >
                  <Pen size={10} />
                </Button>
                <Button
                  variant="outline-danger"
                  className="px-1 py-0 ml-1"
                  onClick={() => {
                    setToBeDeletedItem(contact._id);
                    setShowDeleteAlert(true);
                  }}
                >
                  <Trash size={10} />
                </Button>
              </div>
            )}
          />
        </div>
        <AlertModal
          show={showDeleteAlert}
          onHide={() => setShowDeleteAlert(false)}
          onContinueClick={() => handleDeleteContact(toBeDeletedItem)}
          onDismissClick={() => setShowDeleteAlert(false)}
          alertText={`Are you sure you want to delete this ${activeTab === 'Provider' ? 'provider' : 'department'
            } contact?`}
          progressText={`Deleting ${activeTab === 'Provider' ? 'provider' : 'department'} contact...`}
          showProgress={loading}
        />
        <AddEditCityContactModal
          show={contactActionMeta !== null}
          onHide={() => setContactActionMeta(null)}
          mode={contactActionMeta?.mode || 'add'}
          activeTab={activeTab}
          entityName={activeTab === 'Provider' ? 'Provider Contact' : 'Department Contact'}
          initialValues={getInitialContactFormValues({
            contact: contactActionMeta?.contact || null,
            city,
            state
          })}
          onSubmit={contactActionMeta?.mode === 'edit' ? onUpdateContactFormSubmit : onAddContactFormSubmit}
          loading={loading}
        />
      </div>
    </Container>
  );
};

export default CityContacts;
